import usePrefersReducedMotion from 'hooks/usePrefersReducedMotion'
import { useEffect, useState } from 'react'

/** Which toggle the cursor is resting on before it clicks, if any. */
export type HoveredStage = 'none' | 'on_the_way' | 'delivered'

export type Step1Demo = {
  onTheWayOn: boolean
  deliveredOn: boolean
  hovered: HoveredStage
  fillPct: number
  cursor: {
    leftPct: number
    topPx: number
    visible: boolean
    clicking: boolean
  }
}

export type Step2Demo = { typed: string; done: boolean }

export type Step3Demo = { phase: 'idle' | 'press' | 'loading' | 'done' }

export interface HowItWorksDemoState {
  step1: Step1Demo
  step2: Step2Demo
  step3: Step3Demo
}

const IDLE_STEP1: Step1Demo = {
  onTheWayOn: false,
  deliveredOn: false,
  hovered: 'none',
  fillPct: 0,
  cursor: { leftPct: 44, topPx: 64, visible: true, clicking: false },
}
const IDLE_STEP2: Step2Demo = { typed: '', done: false }
const IDLE_STEP3: Step3Demo = { phase: 'idle' }

const settled = (message: string): HowItWorksDemoState => ({
  step1: {
    onTheWayOn: true,
    deliveredOn: true,
    hovered: 'none',
    fillPct: 100,
    cursor: { leftPct: 44, topPx: 64, visible: false, clicking: false },
  },
  step2: { typed: message, done: true },
  step3: { phase: 'done' },
})

// A self-playing loop illustrating the three setup steps (Sketch prototype
// live-updates-onboarding v29): toggle two stages on, type an example
// message, then publish — looping continuously. Timer-driven (setTimeout/
// setInterval) rather than CSS keyframes, matching the prototype's own
// technique, since the sequence mixes fixed delays with a variable-length
// typing effect that keyframe percentages can't express cleanly.
//
// `message` is the (already-translated) example line step 2 types out —
// owned by the caller so this hook stays locale-agnostic.
export const useHowItWorksDemo = (message: string): HowItWorksDemoState => {
  const prefersReducedMotion = usePrefersReducedMotion()
  const [step1, setStep1] = useState<Step1Demo>(IDLE_STEP1)
  const [step2, setStep2] = useState<Step2Demo>(IDLE_STEP2)
  const [step3, setStep3] = useState<Step3Demo>(IDLE_STEP3)

  useEffect(() => {
    if (prefersReducedMotion) return undefined

    let cancelled = false
    const timeouts: ReturnType<typeof setTimeout>[] = []
    let typingInterval: ReturnType<typeof setInterval> | null = null

    const at = (ms: number, fn: () => void) => {
      timeouts.push(setTimeout(fn, ms))
    }

    const playStep1 = (done: () => void) => {
      setStep1(IDLE_STEP1)
      at(500, () =>
        setStep1(s => ({
          ...s,
          cursor: { ...s.cursor, leftPct: 88, topPx: 193 },
        }))
      )
      // The cursor lands on a toggle and highlights it before it clicks —
      // without that beat the click reads as landing on nothing.
      at(1000, () => setStep1(s => ({ ...s, hovered: 'on_the_way' })))
      at(1250, () =>
        setStep1(s => ({ ...s, cursor: { ...s.cursor, clicking: true } }))
      )
      at(1350, () =>
        setStep1(s => ({
          ...s,
          onTheWayOn: true,
          hovered: 'none',
          fillPct: 50,
          cursor: { ...s.cursor, clicking: false },
        }))
      )
      at(2050, () =>
        setStep1(s => ({ ...s, cursor: { ...s.cursor, topPx: 225 } }))
      )
      at(2500, () => setStep1(s => ({ ...s, hovered: 'delivered' })))
      at(2750, () =>
        setStep1(s => ({ ...s, cursor: { ...s.cursor, clicking: true } }))
      )
      at(2850, () =>
        setStep1(s => ({
          ...s,
          deliveredOn: true,
          hovered: 'none',
          fillPct: 100,
          cursor: { ...s.cursor, clicking: false },
        }))
      )
      at(3250, () =>
        setStep1(s => ({ ...s, cursor: { ...s.cursor, visible: false } }))
      )
      at(4000, done)
    }

    const playStep2 = (done: () => void) => {
      setStep2(IDLE_STEP2)
      let i = 0
      typingInterval = setInterval(() => {
        i += 1
        setStep2({ typed: message.slice(0, i), done: false })
        if (i >= message.length) {
          if (typingInterval) clearInterval(typingInterval)
          typingInterval = null
          setStep2({ typed: message, done: true })
          at(1100, done)
        }
      }, 68)
    }

    const playStep3 = (done: () => void) => {
      setStep3(IDLE_STEP3)
      at(550, () => setStep3({ phase: 'press' }))
      at(800, () => setStep3({ phase: 'loading' }))
      at(1900, () => setStep3({ phase: 'done' }))
      at(3000, done)
    }

    const PAUSE = 750
    const runAll = () => {
      if (cancelled) return
      // Every cycle starts the whole story over, so steps 2 and 3 aren't
      // still sitting on their finished state while step 1 replays.
      setStep2(IDLE_STEP2)
      setStep3(IDLE_STEP3)
      playStep1(() =>
        at(PAUSE, () =>
          playStep2(() => at(PAUSE, () => playStep3(() => at(1500, runAll))))
        )
      )
    }

    runAll()

    return () => {
      cancelled = true
      timeouts.forEach(clearTimeout)
      if (typingInterval) clearInterval(typingInterval)
    }
    // message is a static translated string in practice; re-running the
    // whole timer chain on a locale hot-swap isn't a real scenario worth
    // chasing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefersReducedMotion])

  if (prefersReducedMotion) return settled(message)
  return { step1, step2, step3 }
}

export default useHowItWorksDemo
